/**
 * Leads Inteligentes de Natação — VUKZ Commercial Intelligence
 * Design: fundo branco, texto preto, logo VUKZ — padrão GovernanceClientsPage
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, RefreshCw, Target, Flame, TrendingUp, Users, Trophy,
  X, ChevronRight, Edit3, Plus, Globe,
  BarChart2, CheckCircle, AlertCircle, Zap,
} from 'lucide-react';
import { AZURE } from '../../../services/api';
import { COLORS } from '../../../components/governance/governanceTheme';
import { useGovernanceLang } from '../../../contexts/GovernanceLangContext';

// Temperature config
const TEMP_CONFIG = {
  hot:          { label: 'Quente',           color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: '\uD83D\uDD25' },
  warm:         { label: 'Morno',            color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: '\uD83D\uDFE1' },
  cold:         { label: 'Frio',             color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: '\u2744\uFE0F' },
  low_priority: { label: 'Baixa prioridade', color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', icon: '\u25CB' },
};

const STATUS_CONFIG = {
  new:               { label: 'Novo',             color: '#6b7280', bg: '#f9fafb' },
  prospecting:       { label: 'Prospe\u00E7\u00E3o',  color: '#7c3aed', bg: '#f5f3ff' },
  contacted:         { label: 'Contatado',        color: '#2563eb', bg: '#eff6ff' },
  meeting_scheduled: { label: 'Reuni\u00E3o Marcada', color: '#d97706', bg: '#fffbeb' },
  proposal_sent:     { label: 'Proposta Enviada', color: '#c2410c', bg: '#fff7ed' },
  negotiating:       { label: 'Negociando',       color: '#059669', bg: '#ecfdf5' },
  client:            { label: '\u2713 Cliente',   color: '#065f46', bg: '#d1fae5' },
  discarded:         { label: 'Descartado',       color: '#9ca3af', bg: '#f9fafb' },
  on_hold:           { label: 'Em Espera',        color: '#9a3412', bg: '#fff7ed' },
};

const ACTIVITY_TYPES = [
  { value: 'email',     label: 'E-mail',     icon: '\uD83D\uDCE7' },
  { value: 'call',      label: 'Liga\u00E7\u00E3o', icon: '\uD83D\uDCDE' },
  { value: 'meeting',   label: 'Reuni\u00E3o', icon: '\uD83E\uDD1D' },
  { value: 'demo',      label: 'Demo',        icon: '\uD83D\uDCBB' },
  { value: 'proposal',  label: 'Proposta',    icon: '\uD83D\uDCCB' },
  { value: 'linkedin',  label: 'LinkedIn',    icon: '\uD83D\uDD17' },
  { value: 'whatsapp',  label: 'WhatsApp',    icon: '\uD83D\uDCAC' },
  { value: 'follow_up', label: 'Follow-up',   icon: '\uD83D\uDD01' },
  { value: 'note',      label: 'Nota',        icon: '\uD83D\uDCDD' },
];

const COUNTRY_FLAG = {
  Brasil: '\uD83C\uDDE7\uD83C\uDDF7', Brazil: '\uD83C\uDDE7\uD83C\uDDF7', BRA: '\uD83C\uDDE7\uD83C\uDDF7',
  'United States': '\uD83C\uDDFA\uD83C\uDDF8', USA: '\uD83C\uDDFA\uD83C\uDDF8',
  Australia: '\uD83C\uDDE6\uD83C\uDDFA',
};
const flag = (c) => COUNTRY_FLAG[c] || '\uD83C\uDF0D';

// Badges
const TempBadge = ({ temperature }) => {
  const cfg = TEMP_CONFIG[temperature] || TEMP_CONFIG.cold;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
      borderRadius: 10, background: cfg.bg, color: cfg.color,
      fontSize: 11, fontWeight: 600, border: `1px solid ${cfg.border}`, whiteSpace: 'nowrap',
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 600,
      border: `1px solid ${cfg.color}33`, whiteSpace: 'nowrap',
    }}>{cfg.label}</span>
  );
};

const ScoreBadge = ({ score }) => {
  const s = Math.round(score || 0);
  const color = s >= 80 ? '#dc2626' : s >= 60 ? '#d97706' : s >= 40 ? '#2563eb' : '#6b7280';
  const bg    = s >= 80 ? '#fef2f2' : s >= 60 ? '#fffbeb' : s >= 40 ? '#eff6ff' : '#f9fafb';
  return (
    <span style={{
      display: 'inline-block', minWidth: 38, textAlign: 'center', padding: '2px 7px',
      borderRadius: 8, background: bg, color, fontWeight: 800, fontSize: 13,
      border: `1px solid ${color}44`,
    }}>{s}</span>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, color }) => {
  const c = color || (COLORS && COLORS.primary) || '#111111';
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="rounded-lg p-1.5" style={{ background: `${c}15` }}>
          <Icon size={14} style={{ color: c }} />
        </div>
        <span className="text-xs text-gray-500 font-medium">{label}</span>
      </div>
      <div className="text-2xl font-extrabold text-gray-900">{value ?? '\u2014'}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
};

const MiniBar = ({ value, color }) => (
  <div style={{ height: 4, borderRadius: 2, background: '#f3f4f6', overflow: 'hidden', width: 60, marginBottom: 2 }}>
    <div style={{ width: `${Math.min(100, value || 0)}%`, height: '100%', background: color, borderRadius: 2 }} />
  </div>
);

// Detail Drawer
const ClubDetailDrawer = ({ clubId, onClose, onActivityAdded, onStatusChanged }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showActivity, setShowActivity] = useState(false);
  const [showEditStatus, setShowEditStatus] = useState(false);
  const [activityForm, setActivityForm] = useState({ activity_type: 'call', title: '', description: '' });
  const [statusForm, setStatusForm] = useState({ status: '', next_action: '', assigned_to: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await AZURE.get(`/governance/commercial/swimming-leads/${clubId}`);
      if (data.success) {
        setDetail(data.data);
        setStatusForm({
          status: data.data.commercial_status || 'new',
          next_action: data.data.next_action || '',
          assigned_to: data.data.assigned_to || '',
        });
      }
    } catch (e) { console.error('[leads] detail', e); }
    finally { setLoading(false); }
  }, [clubId]);

  useEffect(() => { load(); }, [load]);

  const submitActivity = async () => {
    if (!activityForm.title.trim()) return;
    setSaving(true);
    try {
      await AZURE.post(`/governance/commercial/swimming-leads/${clubId}/activity`, activityForm);
      setShowActivity(false);
      setActivityForm({ activity_type: 'call', title: '', description: '' });
      load(); onActivityAdded?.();
    } finally { setSaving(false); }
  };

  const submitStatus = async () => {
    setSaving(true);
    try {
      await AZURE.patch(`/governance/commercial/swimming-leads/${clubId}/status`, statusForm);
      setShowEditStatus(false); load(); onStatusChanged?.();
    } finally { setSaving(false); }
  };

  const d = detail;
  const TABS = [
    { id: 'overview', label: 'Vis\u00E3o Geral' },
    { id: 'score',    label: 'Score' },
    { id: 'athletes', label: `Atletas (${d?.total_athletes || 0})` },
    { id: 'history',  label: 'Atividades' },
  ];

  const parseExp = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw); } catch { return [String(raw)]; }
  };

  const primaryColor = (COLORS && COLORS.primary) || '#111111';
  const goldColor    = (COLORS && COLORS.gold)    || '#b48c2c';

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 560, maxWidth: '95vw', height: '100%',
        background: '#fff', borderLeft: '1px solid #e5e7eb',
        overflowY: 'auto', display: 'flex', flexDirection: 'column',
      }}>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw size={24} className="animate-spin text-gray-400" />
          </div>
        ) : !d ? null : (
          <>
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center text-sm font-bold text-gray-600 overflow-hidden flex-shrink-0">
                    {d.logo_url
                      ? <img src={d.logo_url} alt="" className="w-10 h-10 object-contain" onError={e => { e.target.style.display = 'none'; }} />
                      : (d.club_name || '?').substring(0, 2).toUpperCase()
                    }
                  </div>
                  <div>
                    <h2 className="text-lg font-extrabold text-gray-900 leading-tight">{d.club_name}</h2>
                    <p className="text-sm text-gray-500">
                      {flag(d.country)} {d.country}
                      {d.state_or_region && ` \u00B7 ${d.state_or_region}`}
                      {d.city && ` \u00B7 ${d.city}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <ScoreBadge score={d.final_score} />
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X size={18} /></button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <TempBadge temperature={d.commercial_temperature} />
                <StatusBadge status={d.commercial_status} />
                {d.assigned_to && <span className="text-xs text-gray-500">{'\uD83D\uDC64'} {d.assigned_to}</span>}
                <div className="ml-auto flex gap-2">
                  <button onClick={() => setShowEditStatus(!showEditStatus)}
                    className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1 font-medium text-gray-700">
                    <Edit3 size={11} /> Editar status
                  </button>
                  <button onClick={() => setShowActivity(!showActivity)}
                    className="text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 font-bold text-white"
                    style={{ background: goldColor }}>
                    <Plus size={11} /> Atividade
                  </button>
                </div>
              </div>

              {showEditStatus && (
                <div className="mt-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Status</label>
                      <select value={statusForm.status} onChange={e => setStatusForm(p => ({ ...p, status: e.target.value }))}
                        className="w-full text-xs border border-gray-300 rounded-lg p-2 bg-white">
                        {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Respons\u00E1vel</label>
                      <input value={statusForm.assigned_to} onChange={e => setStatusForm(p => ({ ...p, assigned_to: e.target.value }))}
                        placeholder="email@vukz.ai" className="w-full text-xs border border-gray-300 rounded-lg p-2" />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className="text-xs text-gray-500 block mb-1">Pr\u00F3xima A\u00E7\u00E3o</label>
                    <input value={statusForm.next_action} onChange={e => setStatusForm(p => ({ ...p, next_action: e.target.value }))}
                      placeholder="ex: Enviar proposta at\u00E9 20/06" className="w-full text-xs border border-gray-300 rounded-lg p-2" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setShowEditStatus(false)} className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600">Cancelar</button>
                    <button onClick={submitStatus} disabled={saving}
                      className="text-xs px-4 py-1.5 rounded-lg text-white font-bold" style={{ background: primaryColor }}>
                      {saving ? 'Salvando\u2026' : 'Salvar'}
                    </button>
                  </div>
                </div>
              )}

              {showActivity && (
                <div className="mt-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Tipo</label>
                      <select value={activityForm.activity_type} onChange={e => setActivityForm(p => ({ ...p, activity_type: e.target.value }))}
                        className="w-full text-xs border border-gray-300 rounded-lg p-2 bg-white">
                        {ACTIVITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-gray-500 block mb-1">T\u00EDtulo *</label>
                      <input value={activityForm.title} onChange={e => setActivityForm(p => ({ ...p, title: e.target.value }))}
                        placeholder="ex: Liga\u00E7\u00E3o com o diretor t\u00E9cnico"
                        className="w-full text-xs border border-gray-300 rounded-lg p-2" />
                    </div>
                  </div>
                  <textarea value={activityForm.description} onChange={e => setActivityForm(p => ({ ...p, description: e.target.value }))}
                    rows={2} placeholder="Detalhes\u2026"
                    className="w-full text-xs border border-gray-300 rounded-lg p-2 resize-none mb-2" />
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setShowActivity(false)} className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600">Cancelar</button>
                    <button onClick={submitActivity} disabled={saving || !activityForm.title.trim()}
                      className="text-xs px-4 py-1.5 rounded-lg text-white font-bold disabled:opacity-50"
                      style={{ background: goldColor }}>
                      {saving ? 'Salvando\u2026' : 'Registrar'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="border-b border-gray-100 flex px-5">
              {TABS.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`text-sm py-3 px-1 mr-5 border-b-2 font-medium transition-colors ${
                    activeTab === tab.id ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-5 flex-1">
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { icon: Users,     label: 'Atletas',     value: d.total_athletes || 0 },
                      { icon: Zap,       label: 'Ativos 24m',  value: d.active_athletes_24m || 0 },
                      { icon: BarChart2, label: 'Resultados',  value: (d.total_results || 0).toLocaleString('pt-BR') },
                      { icon: Trophy,    label: 'Competi\u00E7\u00F5es', value: d.total_competitions || 0 },
                    ].map(m => (
                      <div key={m.label} className="bg-gray-50 rounded-xl p-3 text-center">
                        <m.icon size={16} className="mx-auto mb-1 text-gray-400" />
                        <div className="text-xl font-extrabold text-gray-900">{m.value}</div>
                        <div className="text-xs text-gray-500">{m.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 flex gap-6 text-sm flex-wrap">
                    <span className="text-gray-500">Medalhas:</span>
                    <span className="font-bold text-yellow-600">{'\uD83E\uDD47'} {d.gold_medals || 0}</span>
                    <span className="font-bold text-gray-500">{'\uD83E\uDD48'} {d.silver_medals || 0}</span>
                    <span className="font-bold text-amber-700">{'\uD83E\uDD49'} {d.bronze_medals || 0}</span>
                    <span className="text-gray-400 text-xs ml-auto">Finais: {d.finals_count || 0} \u00B7 Nacionais: {d.national_competitions_count || 0}</span>
                  </div>
                  {d.next_action && (
                    <div className="border border-blue-200 bg-blue-50 rounded-xl p-3 text-sm text-blue-900">
                      <strong>Pr\u00F3xima a\u00E7\u00E3o:</strong> {d.next_action}
                    </div>
                  )}
                  {d.competitions?.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Competi\u00E7\u00F5es Recentes</div>
                      <div className="space-y-1.5">
                        {d.competitions.map((c, i) => (
                          <div key={i} className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-lg text-sm">
                            <div>
                              <span className="font-medium text-gray-900">{c.name}</span>
                              <span className="text-gray-400 ml-2 text-xs">{c.year}</span>
                              {c.is_major_competition === 1 && <span className="ml-2 text-xs text-amber-600 font-semibold">{'\u2605'} Nacional</span>}
                            </div>
                            <div className="flex gap-3 text-xs text-gray-500">
                              <span>{'\uD83D\uDC65'} {c.club_athletes}</span>
                              {c.golds > 0 && <span className="text-yellow-600 font-bold">{'\uD83E\uDD47'} {c.golds}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'score' && (
                <div className="space-y-4">
                  <div className="text-center py-4 bg-gray-50 rounded-2xl border border-gray-200">
                    <div className="text-xs text-gray-500 uppercase font-semibold mb-1 tracking-wide">Lead Score</div>
                    <div className="text-5xl font-black text-gray-900">{Math.round(d.final_score || 0)}</div>
                    <div className="text-sm text-gray-500 mt-1">de 100 pontos</div>
                    <div className="mt-2 flex justify-center"><TempBadge temperature={d.commercial_temperature} /></div>
                  </div>
                  {d.score_breakdown && (
                    <div className="space-y-3">
                      {Object.entries(d.score_breakdown).map(([key, b]) => (
                        <div key={key}>
                          <div className="flex justify-between items-center mb-1">
                            <div>
                              <span className="text-sm font-semibold text-gray-900">{b.label}</span>
                              <span className="text-xs text-gray-400 ml-2">({Math.round(b.weight * 100)}%)</span>
                            </div>
                            <span className="text-sm font-extrabold text-gray-900">{Math.round(b.score)}/100</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-1">
                            <div className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${b.score}%`, background: b.score >= 70 ? '#059669' : b.score >= 40 ? goldColor : '#2563eb' }} />
                          </div>
                          <div className="text-xs text-gray-400">{b.description}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {parseExp(d.score_explanation).length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                      <div className="text-xs font-semibold text-gray-500 mb-2 uppercase">Detalhes do C\u00E1lculo</div>
                      {parseExp(d.score_explanation).map((e, i) => (
                        <div key={i} className="text-xs text-gray-700 py-1 border-b border-gray-100 last:border-0">\u00B7 {e}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'athletes' && (
                d.top_athletes?.length > 0 ? (
                  <div className="space-y-2">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-3">Top Atletas por Resultados</div>
                    {d.top_athletes.map((a, i) => (
                      <div key={a.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0">{i + 1}</div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-900">{a.name}</div>
                          <div className="text-xs text-gray-400">
                            {a.gender === 'M' ? '\u2642 Masc' : a.gender === 'F' ? '\u2640 Fem' : ''} \u00B7 {a.result_count} resultados
                          </div>
                        </div>
                        {a.golds > 0 && <span className="text-xs text-yellow-600 font-bold">{'\uD83E\uDD47'} {a.golds}</span>}
                      </div>
                    ))}
                  </div>
                ) : <div className="text-center text-gray-400 py-10 text-sm">Nenhum atleta encontrado.</div>
              )}

              {activeTab === 'history' && (
                d.activities?.length > 0 ? (
                  <div className="space-y-2">
                    {d.activities.map(a => {
                      const at = ACTIVITY_TYPES.find(t => t.value === a.activity_type);
                      return (
                        <div key={a.id} className="p-3 border border-gray-100 rounded-xl">
                          <div className="flex justify-between items-start gap-3">
                            <div className="flex gap-2">
                              <span className="text-base">{at?.icon || '\uD83D\uDCDD'}</span>
                              <div>
                                <div className="text-sm font-semibold text-gray-900">{a.title}</div>
                                {a.description && <div className="text-xs text-gray-500 mt-0.5">{a.description}</div>}
                                {a.contact_name && <div className="text-xs text-gray-400">{'\uD83D\uDC64'} {a.contact_name}</div>}
                              </div>
                            </div>
                            <div className="text-xs text-gray-400 text-right flex-shrink-0">
                              {a.activity_date ? new Date(a.activity_date).toLocaleDateString('pt-BR') : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-10 text-sm text-gray-400">
                    <div className="mb-3">Nenhuma atividade registrada.</div>
                    <button onClick={() => setShowActivity(true)}
                      className="text-xs px-4 py-2 rounded-lg text-white font-bold" style={{ background: primaryColor }}>
                      + Registrar primeira atividade
                    </button>
                  </div>
                )
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Main Page
export default function SwimmingLeadsPage() {
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sumLoading, setSumLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [selectedClubId, setSelectedClubId] = useState(null);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterTemp, setFilterTemp] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMinScore, setFilterMinScore] = useState('');
  const [sort, setSort] = useState('score_desc');

  const loadSummary = useCallback(async () => {
    setSumLoading(true);
    try {
      const { data } = await AZURE.get('/governance/commercial/swimming-leads/summary');
      if (data.success) setSummary(data.data);
    } catch (e) { console.error('[leads] summary', e); }
    finally { setSumLoading(false); }
  }, []);

  const loadLeads = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ sort, limit: 300 });
      if (search)         params.set('search', search);
      if (filterCountry)  params.set('country', filterCountry);
      if (filterTemp)     params.set('temperature', filterTemp);
      if (filterStatus)   params.set('status', filterStatus);
      if (filterMinScore) params.set('min_score', filterMinScore);
      const { data } = await AZURE.get(`/governance/commercial/swimming-leads?${params}`);
      if (data.success) setLeads(data.data || []);
      else setError(data.error || 'Erro ao carregar leads');
    } catch (e) {
      setError('Erro de conex\u00E3o com a API');
      console.error('[leads] list', e);
    } finally { setLoading(false); }
  }, [search, filterCountry, filterTemp, filterStatus, filterMinScore, sort]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => {
    const t = setTimeout(() => loadLeads(), search ? 400 : 0);
    return () => clearTimeout(t);
  }, [loadLeads]);

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await AZURE.post('/governance/commercial/swimming-leads/recalculate', {});
      await Promise.all([loadSummary(), loadLeads()]);
    } finally { setRecalculating(false); }
  };

  const countries = [...new Set(leads.map(l => l.country).filter(Boolean))].sort();
  const activeFilters = [filterCountry, filterTemp, filterStatus, filterMinScore].filter(Boolean).length;
  const primaryColor = (COLORS && COLORS.primary) || '#111111';
  const goldColor    = (COLORS && COLORS.gold)    || '#b48c2c';
  const bgColor      = (COLORS && COLORS.bg)      || '#f8fafc';

  return (
    <div className="min-h-screen" style={{ background: bgColor }}>

      <div className="bg-white border-b border-gray-200 px-6 py-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl border-2 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0"
              style={{ borderColor: goldColor }}>
              <img src="/vukz-logo.png" alt="VUKZ" className="w-9 h-9 object-contain"
                onError={e => { e.target.style.display = 'none'; if (e.target.parentElement) { e.target.parentElement.innerHTML = '<span style="font-size:11px;font-weight:900;color:#111">VKZ</span>'; } }} />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-0.5">VUKZ \u00B7 Comercial</div>
              <h1 className="text-2xl font-extrabold text-gray-900">Leads Inteligentes de Nata\u00E7\u00E3o</h1>
              <p className="text-sm text-gray-500">Clubes priorizados por score autom\u00E1tico \u2014 dados reais do banco</p>
            </div>
          </div>
          <button onClick={handleRecalculate} disabled={recalculating}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-sm font-semibold text-gray-700 disabled:opacity-50">
            <RefreshCw size={14} className={recalculating ? 'animate-spin' : ''} />
            {recalculating ? 'Recalculando\u2026' : 'Recalcular Scores'}
          </button>
        </div>

        {!sumLoading && summary && (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-5">
            <KpiCard icon={Globe}       label="Clubes"         value={summary.total_clubs} />
            <KpiCard icon={Flame}       label="Quentes"        value={summary.hot_leads}                   color="#dc2626" />
            <KpiCard icon={TrendingUp}  label="Mornos"         value={summary.warm_leads}                  color={goldColor} />
            <KpiCard icon={Users}       label="Atletas Ativos" value={summary.total_active_athletes}       color="#2563eb" sub="lt. 24 meses" />
            <KpiCard icon={AlertCircle} label="Nao Contatados" value={summary.not_contacted}               color="#6b7280" />
            <KpiCard icon={CheckCircle} label="Clientes"       value={summary.converted_clients || 0}     color="#059669" />
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-4">
          <div className="relative flex-1 min-w-48">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar clube, cidade ou estado..."
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 bg-white text-gray-900" />
          </div>
          <select value={filterCountry} onChange={e => setFilterCountry(e.target.value)}
            className={`text-sm border rounded-lg px-3 py-2 bg-white focus:outline-none ${filterCountry ? 'border-gray-800 font-semibold text-gray-900' : 'border-gray-300 text-gray-500'}`}>
            <option value="">Pais</option>
            {countries.map(c => <option key={c} value={c}>{flag(c)} {c}</option>)}
          </select>
          <select value={filterTemp} onChange={e => setFilterTemp(e.target.value)}
            className={`text-sm border rounded-lg px-3 py-2 bg-white focus:outline-none ${filterTemp ? 'border-gray-800 font-semibold text-gray-900' : 'border-gray-300 text-gray-500'}`}>
            <option value="">Temperatura</option>
            {Object.entries(TEMP_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className={`text-sm border rounded-lg px-3 py-2 bg-white focus:outline-none ${filterStatus ? 'border-gray-800 font-semibold text-gray-900' : 'border-gray-300 text-gray-500'}`}>
            <option value="">Status</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filterMinScore} onChange={e => setFilterMinScore(e.target.value)}
            className={`text-sm border rounded-lg px-3 py-2 bg-white focus:outline-none ${filterMinScore ? 'border-gray-800 font-semibold text-gray-900' : 'border-gray-300 text-gray-500'}`}>
            <option value="">Score min.</option>
            <option value="80">mais de 80 (Quentes)</option>
            <option value="60">mais de 60 (Mornos+)</option>
            <option value="40">mais de 40 (Frios+)</option>
          </select>
          <select value={sort} onChange={e => setSort(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-600 focus:outline-none">
            <option value="score_desc">Score desc</option>
            <option value="athletes_desc">Atletas desc</option>
            <option value="results_desc">Resultados desc</option>
            <option value="name_asc">A a Z Nome</option>
          </select>
          {activeFilters > 0 && (
            <button onClick={() => { setFilterCountry(''); setFilterTemp(''); setFilterStatus(''); setFilterMinScore(''); setSearch(''); }}
              className="text-xs px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-1">
              <X size={11} /> Limpar ({activeFilters})
            </button>
          )}
        </div>
      </div>

      <div className="px-6 py-5">
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm text-gray-500">
            {loading ? 'Carregando...' : `${leads.length} clube${leads.length !== 1 ? 's' : ''}`}
          </span>
          {!summary?.scores_calculated && !loading && (
            <div className="flex items-center gap-1.5 text-xs text-amber-600">
              <AlertCircle size={12} /> Scores nao calculados
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm mb-4">
            {error}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-28 bg-white rounded-xl border border-gray-100 animate-pulse" />)}
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Target size={36} className="mx-auto mb-3 opacity-30" />
            <div className="text-base font-semibold text-gray-600">Nenhum lead encontrado</div>
            <div className="text-sm mt-1">
              {activeFilters > 0 ? 'Tente remover alguns filtros.' : 'Clique em "Recalcular Scores" para gerar os leads.'}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="py-3 px-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide w-10">#</th>
                  <th className="py-3 px-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Clube</th>
                  <th className="py-3 px-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Pais</th>
                  <th className="py-3 px-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Atletas</th>
                  <th className="py-3 px-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Resultados</th>
                  <th className="py-3 px-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Score</th>
                  <th className="py-3 px-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Temperatura</th>
                  <th className="py-3 px-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="py-3 px-2 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {leads.map((lead, idx) => (
                  <tr key={lead.club_id} onClick={() => setSelectedClubId(lead.club_id)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors">
                    <td className="py-3 px-3 text-center text-xs text-gray-400 font-bold">{idx + 1}</td>
                    <td className="py-3 px-3 min-w-48">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0 overflow-hidden">
                          {lead.logo_url
                            ? <img src={lead.logo_url} alt="" className="w-7 h-7 object-contain" onError={e => { e.target.style.display = 'none'; }} />
                            : (lead.club_name || '?').substring(0, 2).toUpperCase()
                          }
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 text-sm leading-tight">{lead.club_name}</div>
                          {lead.is_national_team === 1 && <span className="text-xs text-amber-600 font-bold">{'\u2605'} Selecao</span>}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600">
                      {flag(lead.country)} {lead.country}
                      {lead.state_or_region && <span className="text-gray-400"> / {lead.state_or_region}</span>}
                    </td>
                    <td className="py-3 px-3">
                      <div className="font-semibold text-gray-900">{lead.total_athletes || 0}</div>
                      <div className="text-xs text-gray-400">{lead.active_athletes_24m || 0} ativos</div>
                    </td>
                    <td className="py-3 px-3">
                      <div className="text-gray-900">{(lead.total_results || 0).toLocaleString('pt-BR')}</div>
                      <div className="text-xs text-gray-400">{lead.total_competitions || 0} comp.</div>
                    </td>
                    <td className="py-3 px-3 min-w-24">
                      <div className="mb-1"><ScoreBadge score={lead.final_score} /></div>
                      <MiniBar value={lead.competitive_score} color="#059669" />
                      <MiniBar value={lead.athlete_volume_score} color="#2563eb" />
                    </td>
                    <td className="py-3 px-3"><TempBadge temperature={lead.commercial_temperature} /></td>
                    <td className="py-3 px-3">
                      <StatusBadge status={lead.commercial_status} />
                      {lead.next_action && <div className="text-xs text-gray-400 mt-1 max-w-32 truncate">{lead.next_action}</div>}
                    </td>
                    <td className="py-3 px-2"><ChevronRight size={14} className="text-gray-300" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedClubId && (
        <ClubDetailDrawer
          clubId={selectedClubId}
          onClose={() => setSelectedClubId(null)}
          onActivityAdded={() => loadLeads()}
          onStatusChanged={() => { loadLeads(); loadSummary(); }}
        />
      )}
    </div>
  );
}
